import { useEffect, useState } from "react";
import { Center, Image } from "@chakra-ui/react";

interface PartnerTokenIconProps {
  /** Candidate image sources, tried in order (e.g. static asset, then metadata). */
  sources: (string | undefined)[];
  /** Symbol/name used to derive the monogram when every source fails. */
  label: string;
  /** Accent colour for the monogram fallback. */
  accent: string;
  boxSize?: string;
}

// Renders a token logo, advancing through candidate sources on load error and
// falling back to a coloured monogram badge if none resolve.
export const PartnerTokenIcon = ({
  sources,
  label,
  accent,
  boxSize = "32px",
}: PartnerTokenIconProps) => {
  const candidates = sources.filter((src): src is string => !!src);
  const candidatesKey = candidates.join("|");
  const [index, setIndex] = useState(0);

  // Reset when the candidate list changes (e.g. metadata resolves after mount).
  useEffect(() => {
    setIndex(0);
  }, [candidatesKey]);

  const currentSrc = candidates[index];

  if (!currentSrc) {
    return (
      <Center
        boxSize={boxSize}
        borderRadius="full"
        bg={`${accent}22`}
        border="1px solid"
        borderColor={accent}
        color={accent}
        fontWeight="bold"
        fontSize="sm"
        lineHeight="1"
      >
        {label.charAt(0).toUpperCase()}
      </Center>
    );
  }

  return (
    <Image
      src={currentSrc}
      boxSize={boxSize}
      borderRadius="full"
      alt={label}
      onError={() => setIndex((prev) => prev + 1)}
    />
  );
};
